import {mat4} from 'gl-matrix';
import assert from '../../style-spec/util/assert';

import type {OverscaledTileID} from '../../source/tile_id';
import type SymbolBucket from '../../data/bucket/symbol_bucket';
import type Transform from '../../geo/transform';
import type Projection from './projection';

// Lower bound on the perspective ratio of a symbol still worth placing: clips at 10 times the
// distance of the map center, where the label would draw at 10% the size of the features around
// it. 0.55 === getPerspectiveRatio(cameraToCenter, cameraToCenter * 10). Refer:
// https://github.com/mapbox/mapbox-gl-native/wiki/Text-Rendering#perspective-scaling
// A point behind the camera also lands below it, so the same test rejects those.
export const MIN_COLLISION_PERSPECTIVE_RATIO = 0.55;

function reconstructTileMatrix(transform: Transform, projection: Projection, coord: OverscaledTileID): mat4 {
    // Bucket being rendered is built for different map projection
    // than is currently being used. Reconstruct correct matrices.
    // This code path may happen during a Globe - Mercator transition
    const tileMatrix = projection.createTileMatrix(transform, transform.worldSize, coord.toUnwrapped());
    return mat4.multiply(new Float32Array(16), transform.projMatrix, tileMatrix);
}

export function getCollisionDebugTileProjectionMatrix(coord: OverscaledTileID, bucket: SymbolBucket, transform: Transform): mat4 {
    if (bucket.projection.name === transform.projection.name) {
        assert(coord.projMatrix);
        return coord.projMatrix;
    }
    const tr = transform.clone();
    tr.setProjection(bucket.projection);
    return reconstructTileMatrix(tr, bucket.getProjection(), coord);
}

export function getSymbolTileProjectionMatrix(
    coord: OverscaledTileID,
    bucketProjection: Projection,
    transform: Transform,
): mat4 {
    if (bucketProjection.name === transform.projection.name) {
        assert(coord.projMatrix);
        return coord.projMatrix;
    }
    return reconstructTileMatrix(transform, bucketProjection, coord);
}

export function getSymbolPlacementTileProjectionMatrix(
    coord: OverscaledTileID,
    bucketProjection: Projection,
    transform: Transform,
    runtimeProjection: string,
): mat4 {
    if (bucketProjection.name === runtimeProjection) {
        return transform.calculateProjMatrix(coord.toUnwrapped());
    }
    assert(transform.projection.name === bucketProjection.name);
    return reconstructTileMatrix(transform, bucketProjection, coord);
}
